import {
  Routes,
  Route,
  useNavigationType,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { CssBaseline, CircularProgress, Box } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { lazy, Suspense, useState, useEffect } from "react";
import { AuthProvider, useAuth } from "oidc-react";
import { LanguageProvider } from "./components/LanguageProvider";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import { ToastStateProvider } from "./components/ToastStateProvider";
import { useJiraIssueCollector } from "./hooks/useJiraIssueCollector";
import ClarityConsentBanner, {
  getClarityConsent,
} from "./components/ClarityConsentBanner";
import { TeamMessagesProvider } from "./components/TeamMessagesProvider";
import "./App.css";
import TimeReport from "./pages/TimeReport";

const oidcConfig = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  clientId: `${import.meta.env.VITE_CLIENT_ID}`,
  redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI,
  scope: `openid ${import.meta.env.VITE_API_WORKORDERS_READ_SCOPE}`,
  loadUserInfo: import.meta.env.VITE_OIDC_LOAD_USERINFO === "true",
};

const Attachments = lazy(() => import("./pages/Attachments"));
const Materials = lazy(() => import("./pages/Materials"));
const Notes = lazy(() => import("./pages/Notes"));
const Profile = lazy(() => import("./pages/Profile"));
const Mainpage = lazy(() => import("./pages/Mainpage"));
const NotificationSettings = lazy(
  () => import("./pages/settings/Notifications")
);
const BroadcastView = lazy(() => import("./pages/Broadcast"));
const ExtraReport = lazy(() => import("./pages/ExtraReport"));
const WorkOrderMap = lazy(() => import("./pages/WorkOrderMap"));
const Diary = lazy(() => import("./diary/Diary"));
const TaskWrapper = lazy(() => import("./pages/Task"));
const News = lazy(() => import("./pages/News"));

const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

function Login() {
  const auth = useAuth();

  if (auth && auth.userData) {
    // If user data is available, it means the user is authenticated
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <h2>Login</h2>
      <button onClick={auth.signIn}>Login with OIDC</button>
    </div>
  );
}

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  const theme = createTheme({
    palette: {
      background: {
        default: "#eee",
      },
    },
    // MUI v6 specific defaults
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: false,
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 1,
        },
      },
    },
  });

  useEffect(() => {
    let title = "";
    let metaDescription = "";

    switch (pathname) {
      case "/":
        title = "TAPP";
        metaDescription = "";
        break;
      case "/attachments":
        title = "TAPP-Attachments";
        metaDescription = "";
        break;
      case "/materials":
        title = "TAPP-Materials";
        metaDescription = "";
        break;
      case "/Notes":
        title = "TAPP-Notes";
        metaDescription = "";
        break;
      case "/task":
        title = "Task";
        metaDescription = "";
        break;
      case "/report":
        title = "Report";
        metaDescription = "";
        break;
      case "/news":
        title = "News";
        metaDescription = "";
        break;
      case "/time":
        title = "Time Report";
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }
  }, [pathname]);

  const storeCurrentPath = () => {
    const currentPath = location.pathname + location.search + location.hash;
    sessionStorage.setItem("currentPath", currentPath);
  };

  const redirectToPreviousPath = () => {
    navigate(sessionStorage.getItem("currentPath") ?? "/");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider
        {...oidcConfig}
        onBeforeSignIn={storeCurrentPath}
        onSignIn={redirectToPreviousPath}
      >
        <LanguageProvider>
          <ToastStateProvider>
            <AppContent />
          </ToastStateProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const auth = useAuth();
  useJiraIssueCollector(auth?.userData);
  const isAuthenticated = auth && auth.userData;
  const [consentBannerOpen, setConsentBannerOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && getClarityConsent() === null) {
      setConsentBannerOpen(true);
    }
  }, [isAuthenticated]);

  if (isAuthenticated) {
    return (
      <TeamMessagesProvider>
        <PushNotificationPrompt />
        <Suspense fallback={<LoadingFallback />}>
          <ClarityConsentBanner
            open={consentBannerOpen}
            onClose={() => setConsentBannerOpen(false)}
          />
          <Routes>
            <Route path="/" element={<Mainpage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/attachments/:wono" element={<Attachments />} />
            <Route path="/materials/:wono" element={<Materials />} />
            <Route path="/notes/:wono/:tabName?" element={<Notes />} />
            <Route path="/task/:wono" element={<TaskWrapper />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/settings/notifications"
              element={<NotificationSettings />}
            />
            <Route path="/broadcast" element={<BroadcastView />} />
            <Route path="/workordermap/:mode?" element={<WorkOrderMap />} />
            <Route path="/report/:wono" element={<ExtraReport />} />
            <Route path="/diary/:wono" element={<Diary />} />
            <Route path="/news" element={<News />} />
            <Route path="/time" element={<TimeReport />} />
          </Routes>
        </Suspense>
      </TeamMessagesProvider>
    );
  } else {
    return <></>;
  }
}

export default App;
